import React from 'react';
import { motion } from 'framer-motion';
import { Wallet, Shield, CreditCard, TrendingUp } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';

const WalletPage: React.FC = () => {
  const { isConnected, connectWallet, address, balance } = useWallet();

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 bg-accent-wood/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Wallet className="w-10 h-10 text-accent-wood" />
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6">
            Your <span className="text-accent-wood">Wallet</span>
          </h1>
          <p className="text-xl text-primary-300 max-w-3xl mx-auto">
            Manage your digital assets and family legacy securely. Connect your wallet to start preserving your precious items.
          </p>
        </motion.div>

        {/* Wallet Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8 mb-12"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-display font-bold text-white mb-2">
                Wallet Status
              </h2>
              <p className="text-primary-300">
                {isConnected ? 'Connected' : 'Not connected'}
              </p>
            </div>
            {!isConnected ? (
              <button
                onClick={connectWallet}
                className="bg-accent-wood hover:bg-accent-wood-light text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2"
              >
                <Wallet className="w-5 h-5" />
                Connect Wallet
              </button>
            ) : (
              <div className="text-right">
                <p className="text-sm text-primary-400">Address</p>
                <p className="font-mono text-white">{address}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Wallet Stats */}
        {isConnected && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-primary-800 to-primary-700 rounded-xl border border-primary-600 p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent-wood/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-accent-wood" />
                </div>
                <div>
                  <p className="text-sm text-primary-400">Balance</p>
                  <p className="text-2xl font-bold text-white">
                    {balance ? `${parseFloat(balance).toFixed(4)}` : '0.0000'}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gradient-to-br from-accent-wood/10 to-accent-wood/20 rounded-xl border border-accent-wood/30 p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent-wood/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-accent-wood" />
                </div>
                <div>
                  <p className="text-sm text-primary-400">Legacy Items</p>
                  <p className="text-2xl font-bold text-white">0</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent-wood/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-accent-wood" />
                </div>
                <div>
                  <p className="text-sm text-primary-500">Collections</p>
                  <p className="text-2xl font-bold text-primary-900">0</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Content Sections */}
        <div className="space-y-12">
          {/* Section 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg border border-primary-200 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Manage Your <span className="text-accent-wood">Assets</span>
            </h2>
            <p className="text-primary-600 text-lg">
              Content coming soon... This section will allow users to view and manage their legacy items and collections.
            </p>
          </motion.div>

          {/* Section 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-accent-wood/5 to-accent-wood/10 rounded-2xl border border-accent-wood/20 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Security <span className="text-accent-wood">Settings</span>
            </h2>
            <p className="text-primary-600 text-lg">
              Content coming soon... This section will provide wallet security settings and backup options.
            </p>
          </motion.div>

          {/* Section 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg border border-primary-200 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Transaction <span className="text-accent-wood">History</span>
            </h2>
            <p className="text-primary-600 text-lg">
              Content coming soon... This section will show the history of all wallet transactions and legacy item activities.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WalletPage; 